import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Star, Rocket, Zap, FileText, Code, Cpu } from 'lucide-react';
import logo from '../Images/ShowPic.png';

// Static particle data — defined outside component so never re-randomised
const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  size: [2, 3, 1.5, 2.5, 1, 3, 2, 1.5, 2.5, 2, 1, 3, 1.5, 2, 2.5, 1, 3, 2][i],
  x: [5, 12, 22, 35, 48, 60, 72, 83, 91, 18, 42, 65, 78, 30, 55, 88, 10, 70][i],
  y: [15, 40, 8, 65, 30, 80, 20, 55, 10, 75, 45, 90, 35, 60, 5, 50, 85, 25][i],
  duration: [18, 22, 15, 25, 20, 16, 28, 19, 24, 21, 17, 26, 23, 18, 27, 20, 15, 22][i],
  delay: [0, 2, 4, 1, 3, 5, 0.5, 2.5, 4.5, 1.5, 3.5, 0.2, 2.8, 4.2, 1.2, 3.2, 5.5, 0.8][i],
}));

const BINARY_ROWS = Array.from({ length: 8 }, (_, i) =>
  Array.from({ length: 12 }, (_, j) => ((i * 13 + j * 7 + 3) % 2))
);

const Hero = ({ homeRef, setActiveSection, handleHover, handleHoverEnd }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    if (isMobile) return; // skip mouse tracking on mobile
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  return (
    <section id="home" ref={homeRef} className="hero">
      {/* Background layer */}
      <div className="hero-background">
        {/* Static particles — no Math.random() in render */}
        {PARTICLES.map((p) => (
          <motion.div
            key={p.id}
            className="floating-particle"
            style={{
              position: 'absolute',
              width: `${p.size}px`,
              height: `${p.size}px`,
              background: p.id % 2 === 0
                ? 'rgba(102, 126, 234, 0.5)'
                : 'rgba(240, 147, 251, 0.4)',
              borderRadius: '50%',
              left: `${p.x}%`,
              top: `${p.y}%`,
              zIndex: 1,
            }}
            animate={{ y: [-20, -80, -20], opacity: [0, 0.7, 0] }}
            transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
          />
        ))}

        {/* Gradient orbs */}
        <motion.div
          className="gradient-orb-1"
          animate={{ x: [0, 25, -15, 0], y: [0, -30, 20, 0], scale: [1, 1.15, 0.9, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transform: `translate(${mousePosition.x * 18}px, ${mousePosition.y * 18}px)` }}
        />
        <motion.div
          className="gradient-orb-2"
          animate={{ x: [0, -30, 18, 0], y: [0, 25, -22, 0], scale: [1, 0.85, 1.2, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transform: `translate(${mousePosition.x * -12}px, ${mousePosition.y * -12}px)` }}
        />
        <motion.div
          className="gradient-orb-3"
          animate={{ x: [0, 15, -22, 10, 0], y: [0, -18, 0, 28, 0], scale: [1, 1.3, 0.7, 1.05, 1] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transform: `translate(${mousePosition.x * 8}px, ${mousePosition.y * 20}px)` }}
        />

        {/* Floating icons — 3 only, simpler */}
        {[Code, Cpu, Star].map((Icon, index) => (
          <motion.div
            key={index}
            style={{ position: 'absolute', left: `${20 + index * 28}%`, top: `${18 + (index % 2) * 38}%`, zIndex: 2 }}
            animate={{ opacity: [0, 0.5, 0], y: [-40, -160], scale: [0, 1.2, 0] }}
            transition={{ duration: 9, repeat: Infinity, delay: index * 2.5, ease: 'easeOut' }}
          >
            <Icon size={18} style={{ color: '#667eea' }} />
          </motion.div>
        ))}
      </div>

      {/* Main content */}
      <motion.div
        className="hero-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.0 }}
        style={{
          transform: isMobile ? undefined : `translate(${mousePosition.x * 4}px, ${mousePosition.y * 4}px)`,
          minHeight: 'calc(100vh - 80px)',
        }}
      >
        {/* Left: text */}
        <div className="hero-text" style={{ flex: 1, maxWidth: '600px' }}>
          <motion.div
            className="hero-greeting"
            initial={{ x: -40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.7, type: 'spring', bounce: 0.5 }}
          >
            <Star className="greeting-icon" />
            <span>Welcome to My World of Innovation</span>
          </motion.div>

          <motion.h1
            className="hero-title"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.8, type: 'spring', bounce: 0.35 }}
          >
            I'm{' '}
            <motion.span
              className="hero-name"
              animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
              style={{
                background: 'linear-gradient(90deg, #667eea, #764ba2, #f093fb, #667eea)',
                backgroundSize: '300% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Waleed Abdullah
            </motion.span>
          </motion.h1>

          <motion.div
            className="hero-roles"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
          >
            {['Software Engineer', 'AI/ML Engineer', 'UI/UX Designer'].map((role) => (
              <motion.div
                key={role}
                className="role-tag"
                whileHover={{ y: -5, scale: 1.06, boxShadow: '0 10px 24px -4px rgba(102,126,234,0.35)' }}
                whileTap={{ scale: 0.96 }}
              >
                {role}
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            className="hero-subtitle"
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.7 }}
          >
            Transforming ideas into digital experiences through cutting edge technology and
            purposeful design. Building the future, one line of code at a time.
          </motion.p>

          <motion.div
            className="hero-buttons"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.5 }}
          >
            <motion.button
              className="secondary-button"
              onClick={() => window.open('https://drive.google.com/file/d/1on68LZflefnmU-Px6aCTAorbfCLa4iqa/view?usp=share_link', '_blank')}
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onMouseEnter={() => handleHover('secondary')}
              onMouseLeave={handleHoverEnd}
            >
              <FileText className="button-icon" /> View CV
            </motion.button>

            <motion.button
              className="primary-button"
              onClick={() => { setActiveSection('projects'); document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' }); }}
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onMouseEnter={() => handleHover('primary')}
              onMouseLeave={handleHoverEnd}
            >
              <Rocket className="button-icon" />
              Explore My Universe
              <motion.div className="button-glow" animate={{ opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 3, repeat: Infinity }} />
            </motion.button>

            <motion.button
              className="secondary-button"
              onClick={() => { setActiveSection('contact'); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onMouseEnter={() => handleHover('secondary')}
              onMouseLeave={handleHoverEnd}
            >
              <Zap className="button-icon" /> Let's Collaborate
            </motion.button>
          </motion.div>
        </div>

        {/* Right: orb visual */}
        <div className="hero-visual">
          <motion.div
            className="hero-animation"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.7, duration: 1.0, type: 'spring', bounce: 0.35 }}
            style={{ transform: isMobile ? undefined : `translate(${mousePosition.x * -8}px, ${mousePosition.y * -8}px)` }}
          >
            {/* Lightweight binary grid — 8x12 = 96 spans, not 375 */}
            <div className="code-matrix">
              {BINARY_ROWS.map((row, i) => (
                <motion.div
                  key={i}
                  className="code-row"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.06, duration: 0.6 }}
                >
                  {row.map((bit, j) => (
                    <motion.span
                      key={j}
                      className="code-char"
                      animate={{ opacity: [0.15, 0.9, 0.15], color: ['#667eea', '#f093fb', '#667eea'] }}
                      transition={{ duration: 3.5 + (i + j) * 0.12, repeat: Infinity, delay: (i + j) * 0.04 }}
                    >
                      {bit}
                    </motion.span>
                  ))}
                </motion.div>
              ))}
            </div>

            {/*
              Centre orb, rebuilt with explicit fixed sizing so the photo is a
              true circle no matter what the surrounding CSS does. The photo
              itself stays still; everything that spins lives in layers drawn
              outside it, in its own bigger box, so the motion is obviously
              happening around the picture rather than to it.
            */}
            <div
              className="center-orb"
              style={{
                position: 'relative',
                width: '300px',
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Outer spinning halo — a dashed gradient ring, clockwise */}
              <motion.div
                style={{
                  position: 'absolute',
                  width: '300px',
                  height: '300px',
                  borderRadius: '50%',
                  border: '2px dashed rgba(240,147,251,0.6)',
                  boxSizing: 'border-box',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 16, repeat: Infinity, ease: 'linear' }}
              />

              {/* Second spinning halo, slightly smaller, counter-clockwise,
                  made of short gradient arcs for a "cute" segmented look */}
              <motion.div
                style={{
                  position: 'absolute',
                  width: '262px',
                  height: '262px',
                  borderRadius: '50%',
                  background:
                    'conic-gradient(from 0deg, transparent 0deg, #667eea 25deg, transparent 55deg, transparent 90deg, #f093fb 115deg, transparent 145deg, transparent 180deg, #667eea 205deg, transparent 235deg, transparent 270deg, #f093fb 295deg, transparent 325deg, transparent 360deg)',
                  WebkitMask: 'radial-gradient(closest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
                  mask: 'radial-gradient(closest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
                }}
                animate={{ rotate: -360 }}
                transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
              />

              {/* Soft glow pulse, sits behind the photo */}
              <motion.div
                style={{
                  position: 'absolute',
                  width: '230px',
                  height: '230px',
                  borderRadius: '50%',
                }}
                animate={{
                  boxShadow: [
                    '0 0 50px rgba(102,126,234,0.45)',
                    '0 0 90px rgba(240,147,251,0.7)',
                    '0 0 50px rgba(102,126,234,0.45)',
                  ],
                  scale: [1, 1.05, 1],
                }}
                transition={{ duration: 4, repeat: Infinity }}
              />

              {/* Orbiting satellite dots — small, spaced out, clearly outside the photo */}
              {[0, 120, 240].map((angleDeg, idx) => (
                <motion.div
                  key={angleDeg}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: idx === 0 ? '14px' : '9px',
                    height: idx === 0 ? '14px' : '9px',
                    marginTop: idx === 0 ? '-7px' : '-4.5px',
                    marginLeft: idx === 0 ? '-7px' : '-4.5px',
                    borderRadius: '50%',
                    background: idx % 2 === 0
                      ? 'radial-gradient(circle, #f093fb, #764ba2)'
                      : 'radial-gradient(circle, #667eea, #4c56b3)',
                    boxShadow: '0 0 12px rgba(240,147,251,0.9)',
                    transformOrigin: '150px 0px',
                    rotate: `${angleDeg}deg`,
                  }}
                  animate={{ rotate: [angleDeg, angleDeg + 360] }}
                  transition={{ duration: 12 + idx * 4, repeat: Infinity, ease: 'linear' }}
                />
              ))}

              {/* The photo itself: fixed square box, forced perfect circle,
                  never rotates. objectPosition keeps the crop favouring the
                  head and shoulders. */}
              <motion.div
                style={{
                  position: 'relative',
                  width: '210px',
                  height: '210px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  flexShrink: 0,
                  border: '3px solid rgba(255,255,255,0.15)',
                  zIndex: 2,
                }}
                whileHover={{ scale: 1.06 }}
                transition={{ type: 'spring', stiffness: 200 }}
              >
                <img
                  src={logo}
                  alt="Waleed Abdullah"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: '50% 15%',
                    display: 'block',
                  }}
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator, desktop only. On phones the hero content stacks
          taller than the viewport and this was landing right on top of the
          buttons, so it is simply not needed on mobile in the first place. */}
      {!isMobile && (
        <motion.div
          className="scroll-indicator"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2, duration: 0.6, type: 'spring' }}
        >
          <motion.div className="scroll-text" animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2.5, repeat: Infinity }}>
            Discover More
          </motion.div>
          <motion.div animate={{ y: [0, 12, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            <ChevronDown className="scroll-icon" />
          </motion.div>
          <motion.div className="scroll-line" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 2, repeat: Infinity }} />
        </motion.div>
      )}
    </section>
  );
};

export default Hero;